import {
  ClipboardIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  PuzzlePieceIcon,
  Square2StackIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Dropdown, { Option } from 'components/commercetools-ui/atoms/dropdown';
import React, { useEffect, useState } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet, largeDesktop, desktop, mediumDesktop, semiLargeDesktop, extraLargeDesktop } from 'helpers/utils/screensizes';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import useTouchDevice from 'helpers/hooks/useTouchDevice';

interface SidebarPDPProps {
  hideAdvice?: boolean;
  showComponents?: boolean;
  showSpareParts?: boolean;
}

export const sidebarSections = [
  {
    name: 'Product overview',
    icon: <InformationCircleIcon className="w-28 text-[#666] 2xl:w-32" />,
    value: 'product-overview-scroll',
  },
  { name: 'Technical info', icon: <ClipboardIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'specifier-info' },
  { name: 'Components', icon: <PuzzlePieceIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'components-section' },
  { name: 'Spare Parts', icon: <WrenchScrewdriverIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'spare-parts-section' },
  { name: 'Advice', icon: <MegaphoneIcon className="w-28 text-[#666] 2xl:w-32" />, value: 'advice-section' },
  {
    name: 'Related Products',
    icon: <Square2StackIcon className="w-28 text-[#666] 2xl:w-32" />,
    value: 'related-products',
  },
  //   { name: 'FAQs', icon: <QuestionMarkCircleIcon className="w-32 text-[#666]" />, value: 'faq-section' },
];


function SidebarPDP({ hideAdvice, showComponents = true, showSpareParts = true }: SidebarPDPProps) {
  const [activeSection, setActiveSection] = useState(0);
  const [isTablet] = useMediaQuery(tablet);
  const [isLargeDesktop] = useMediaQuery(semiLargeDesktop);
  const [isHugeLargeDesktop] = useMediaQuery(extraLargeDesktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [isDesktop] = useMediaQuery(desktop);
  const { isTouchDevice } = useTouchDevice();
  const [distanceToTop, setDistanceToTop] = useState(1000);
  const [sidebarHeight, setSidebarHeight] = useState(0);
  const [hasComponentsSection, setHasComponentsSection] = useState(false);
  const [hasSparePartsSection, setHasSparePartsSection] = useState(false);
  const width = useWindowWidth();

  // Check if sections actually exist in the DOM
  useEffect(() => {
    const checkSections = () => {
      setHasComponentsSection(!!document.getElementById('components-section'));
      setHasSparePartsSection(!!document.getElementById('spare-parts-section'));
    };
    
    // Check immediately
    checkSections();
    
    // Check periodically to catch async-loaded sections
    const interval = setInterval(checkSections, 500);
    
    return () => clearInterval(interval);
  }, []);

  const visibleSections = sidebarSections.filter((section) => {
    if (hideAdvice && section.value === 'advice-section') return false;
    if (section.value === 'components-section') {
      if (!showComponents || !hasComponentsSection) return false;
    }
    if (section.value === 'spare-parts-section') {
      if (!showSpareParts || !hasSparePartsSection) return false;
    }
    return true;
  });

  const scrollTo = (ele: HTMLElement) => {
    const position = ele.getBoundingClientRect();
    window.scrollTo({
      top: position.top + window.scrollY - (isHugeLargeDesktop? 115 : isLargeDesktop ? 90 : isMediumDesktop ? 50 : isTablet? 75: 100),
      behavior: 'smooth',
    });
  };
  const scrollToDesktop = (id: string) => {
    const ele = document.getElementById(id);
    if (ele) {
      scrollTo(ele);
    }
  };
  const scrollToMobile = (option: Option) => {
    const ele = document.getElementById(option.value.toString());
    if (ele) {
      scrollTo(ele);
    }
  };


  const handleClick = (index: React.SetStateAction<number>, id: string) => {
    scrollToDesktop(id);
  };

  useEffect(() => {
     const handleScroll = () => {
      // Re-check on scroll because PDP sections can load async (Algolia / CT data)
      // checkSections();

      const element1 = document.getElementById('product-overview');
      const element2 = document.getElementById('specifier-info-title');
      const element3 = document.getElementById('components-section');
      const element4 = document.getElementById('spare-parts-section');
      const element5 = document.getElementById('advice-section');
      const element6 = document.getElementById('related-products-title');
      //   const element5 = document.getElementById('faq-section');

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const viewportThreshold = 200; // Distance from top of viewport to consider section active
      const sections = [];

      // Check each section and calculate its position relative to viewport
      if (element1) {
        const rect = element1.getBoundingClientRect();
        sections.push({ 
          index: 0, 
          top: rect.top + scrollTop, 
          viewportTop: rect.top,
          element: element1 
        });
        setDistanceToTop(rect.top + scrollTop);
      }
      if (element2) {
        const rect = element2.getBoundingClientRect();
        sections.push({ 
          index: 1, 
          top: rect.top + scrollTop, 
          viewportTop: rect.top,
          element: element2 
        });
      }
      if (element3 && visibleSections.some(s => s.value === 'components-section')) {
        const rect = element3.getBoundingClientRect();
        const idx = visibleSections.findIndex(s => s.value === 'components-section');
        if (idx !== -1) {
          sections.push({ 
            index: idx, 
            top: rect.top + scrollTop, 
            viewportTop: rect.top,
            element: element3 
          });
        }
      }
      if (element4 && visibleSections.some(s => s.value === 'spare-parts-section')) {
        const rect = element4.getBoundingClientRect();
        const idx = visibleSections.findIndex(s => s.value === 'spare-parts-section');
        if (idx !== -1) {
          sections.push({ 
            index: idx, 
            top: rect.top + scrollTop, 
            viewportTop: rect.top,
            element: element4 
          });
        }
      }
      if (element5 && visibleSections.some(s => s.value === 'advice-section')) {
        const rect = element5.getBoundingClientRect();
        const idx = visibleSections.findIndex(s => s.value === 'advice-section');
        if (idx !== -1) {
          sections.push({ 
            index: idx, 
            top: rect.top + scrollTop, 
            viewportTop: rect.top,
            element: element5 
          });
        }
      }
      if (element6 && visibleSections.some(s => s.value === 'related-products')) {
        const rect = element6.getBoundingClientRect();
        const idx = visibleSections.findIndex(s => s.value === 'related-products');
        if (idx !== -1) {
          sections.push({ 
            index: idx, 
            top: rect.top + scrollTop, 
            viewportTop: rect.top,
            element: element6 
          });
        }
        setSidebarHeight(rect.top + scrollTop);
      }

      // Find the active section: the one whose top is closest to the viewport threshold
      if (sections.length > 0) {
        // Sort by position on page (top to bottom)
        sections.sort((a, b) => a.top - b.top);
        
        // Filter to only sections whose top is within reasonable range of viewport top
        // This ensures we only consider sections that are actually in the active viewing area
        const candidateSections = sections.filter(s => 
          s.viewportTop >= -100 && s.viewportTop <= viewportThreshold + 100
        );
        
        if (candidateSections.length > 0) {
          // Among candidates, pick the one with smallest viewportTop (closest to top of viewport)
          // This ensures Components is selected over Related Products when both are visible
          const activeSection = candidateSections.reduce((prev, current) => 
            (prev.viewportTop < current.viewportTop) ? prev : current
          );
          
          setActiveSection(activeSection.index);
        } else {
          // If no sections are in the active range, find the one closest to threshold
          const closestSection = sections.reduce((prev, current) => {
            const prevDist = Math.abs(prev.viewportTop - viewportThreshold);
            const currentDist = Math.abs(current.viewportTop - viewportThreshold);
            return (prevDist < currentDist) ? prev : current;
          });
          setActiveSection(closestSection.index);
        }
      }
    };


    // Call on mount to set initial active section
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMediumDesktop, isTablet, isDesktop, visibleSections]);

  return (
    <>
      {width !== null && width < 1024 ? (
        <div
        //   style={{ marginTop: `${distanceToTop / 20 - 30}rem` }} 
          className="absolute w-full left-full z-10 mb-48 w-screen -translate-x-full px-24 lg:grid lg:grid-cols-12"
        >
          <Dropdown
            selectOptions={visibleSections}
            selectOnChange={scrollToMobile}
            selectDropdownClassNames="p-16 w-full"
          ></Dropdown>
        </div>
      ) : (
        <div
          style={{ height: `${sidebarHeight / 15 - 6}rem` }}
          className="relative mt-[5rem] "
        >
          <div className="h-full flex-col text-18 font-semibold leading-6 ">
            <div className="sticky left-0 top-[10rem] 2xl:top-[13rem] !z-[40] mb-40 w-fit">
              {visibleSections.map((section, index) => (
                <div
                  className={`flex items-center gap-12 py-24 pl-18 hover:cursor-pointer 2xl:pl-32 ${
                    index === activeSection ? 'border-l-4 border-[#090909]' : 'border-l-4 border-[#F3EDE9]'
                  }`}
                  key={index}
                  onClick={() => handleClick(index, section.value)}
                >
                  {section.icon}
                  <span className="text-16 font-semibold leading-6 text-primary-black lg:text-14 3xl:text-18">
                    {section.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SidebarPDP;
